import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { exportToExcel } from "../utils/exportExcel";
import { useNavigate } from "react-router-dom";
import AlertModal from "../components/common/AlertModal";
import ConfirmModal from "../components/common/ConfirmModal";

const Admin = () => {
  const [confirmations, setConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState("info");
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, "confirmations"),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setConfirmations(data);
    } catch (err) {
      console.error("Error fetching documents: ", err);
      setError("Failed to load data. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminAuthenticated");
    navigate("/admin-login");
  };



  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      if (selectedIds.length === 0) {
        setError("No confirmations selected.");
        setIsDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      // Use batch delete for better performance
      const batch = writeBatch(db);
      selectedIds.forEach((id) => {
        batch.delete(doc(db, "confirmations", id));
      });

      await batch.commit();

      // Update local state - remove deleted items
      setConfirmations((prev) =>
        prev.filter((c) => !selectedIds.includes(c.id))
      );
      const deletedCount = selectedIds.length;
      setSelectedIds([]);
      setShowDeleteModal(false);

      // Show success message
      setAlertMessage(`Successfully deleted ${deletedCount} confirmation(s).`);
      setAlertType("success");
    } catch (err) {
      console.error("Error deleting confirmations: ", err);
      setShowDeleteModal(false);
      setAlertMessage("Failed to delete confirmations. Please try again.");
      setAlertType("error");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (typeof timestamp.toDate === "function") {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
      if (isNaN(date.getTime())) return String(timestamp);
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}.${month}.${year} • ${hours}:${minutes}`;
  };

  return (
    <div className="min-h-screen bg-test font-serif">
      <div className="mx-auto max-w-[1100px] px-4 py-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
            <h1 className="text-3xl font-bold text-gray-900 text-center md:text-left">
              Wedding Admin Dashboard
            </h1>

            {/* Primary Navigation - Top Right */}
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 rounded-xl text-white font-serif transition-colors"
              >
                Go Home
              </button>
              <button
                onClick={handleLogout}
                className="px-6 py-3 bg-red-800 hover:bg-red-900 rounded-xl text-white font-serif transition-colors"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Table Action Buttons - Below Header */}
          <div className="flex flex-wrap justify-center md:justify-end gap-3">
            <button
              onClick={fetchData}
              className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-800 font-serif transition-colors text-sm"
            >
              Refresh
            </button>
            <button
              onClick={() => {
                try {
                  exportToExcel(confirmations);
                } catch (error) {
                  setAlertMessage(error.message);
                  setAlertType("warning");
                }
              }}
              className="px-5 py-2.5 bg-blue1 text-white font-serif rounded-lg hover:opacity-90 transition-opacity shadow-sm text-sm"
            >
              Download Excel
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={selectedIds.length === 0}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg text-white font-serif transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Delete Selected ({selectedIds.length})
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : confirmations.length === 0 ? (
          <div className="bg-white shadow-xl rounded-lg p-8 text-center border border-gray-300">
            <p className="text-gray-500 text-lg">
              No confirmations received yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {confirmations.map((row, index) => (
              <div
                key={row.id}
                className={`
                  bg-white shadow-lg rounded-xl border-2 overflow-hidden transition-all
                  ${selectedIds.includes(row.id)
                    ? "border-blue-500 shadow-blue-200"
                    : "border-gray-300 hover:border-gray-400"
                  }
                `}
              >
                {/* Card Header with Checkbox and Name */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-5 py-4 border-b border-gray-300 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => handleSelectOne(row.id)}
                      className="w-5 h-5 cursor-pointer rounded border-gray-400"
                    />
                    <h3 className="text-xl font-bold text-gray-900">
                      {row.name}
                    </h3>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatDate(row.createdAt)}
                  </div>
                </div>

                {/* Card Body with Details in Grid */}
                <div className="p-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Contact Information */}
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        Email
                      </p>
                      <p className="text-base text-gray-900 break-all">
                        {row.email}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        Phone
                      </p>
                      <p className="text-base text-gray-900">{row.phone}</p>
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        Side
                      </p>
                      <p className="text-base text-gray-900 capitalize">
                        {row.invitationSide}
                      </p>
                    </div>

                    {/* Attendance Status */}
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        Attendance
                      </p>
                      <p
                        className={`text-base font-semibold capitalize ${row.attendance === "yes"
                          ? "text-green-600"
                          : row.attendance === "no"
                            ? "text-red-600"
                            : "text-yellow-600"
                          }`}
                      >
                        {row.attendance === "not_sure"
                          ? "Not Sure"
                          : row.attendance}
                      </p>
                    </div>

                    {/* Plus One */}
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        Plus One
                      </p>
                      {row.plusOne ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          Yes ({row.plusOneCount || 1})
                        </span>
                      ) : (
                        <span className="text-gray-400 text-base">No</span>
                      )}
                    </div>

                    {/* Children */}
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        Children
                      </p>
                      {row.children ? (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 w-fit">
                            Yes ({row.childrenCount || 0})
                          </span>
                          {row.childrenAges && row.childrenAges.length > 0 && (
                            <span className="text-xs text-gray-600">
                              Ages:{" "}
                              {Array.isArray(row.childrenAges)
                                ? row.childrenAges.join(", ")
                                : row.childrenAges}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-base">No</span>
                      )}
                    </div>
                  </div>

                  {/* Notes Section - Full Width */}
                  {row.requirements && row.requirements !== "-" && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Special Requirements / Notes
                      </p>
                      <p className="text-base text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                        {row.requirements}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <ConfirmModal
            title="Delete Selected Confirmations?"
            message={`Are you sure you want to delete ${selectedIds.length} selected confirmation(s)?\n\nThis action cannot be undone!`}
            onConfirm={handleDeleteSelected}
            onCancel={() => setShowDeleteModal(false)}
            confirmText="Delete Selected"
            cancelText="Cancel"
            isLoading={isDeleting}
          />
        )}

        {/* Alert Modal */}
        {alertMessage && (
          <AlertModal
            message={alertMessage}
            type={alertType}
            onClose={() => setAlertMessage(null)}
          />
        )}
      </div>
    </div>
  );
};

export default Admin;
